import {
  formatDateColumnValue,
  formatNumberColumnValue,
  getTableCellPrimitiveValue,
  isTableCellData,
} from '@doss/common-all'
import { createContext, PropsWithChildren, useCallback, useContext } from 'react'
import {
  DataType,
  LookupProviderColumnFragment,
  ResolvedSourceColumnFragment,
} from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { getEnumValueColorAndIcon } from '~/components/shared/enum-options'
import {
  enumSourceForLookupColumn,
  LookupEnumRendering,
  LookupEnumSource,
} from '~/components/shared/lookup-enum'

export type LookupProviderColumn = LookupProviderColumnFragment & ResolvedSourceColumnFragment

/** Cell shape for display; cache/CDC often set `data` while `userVisibleValue` stays null. */
export type MinimalLookupTableRowCell = {
  id: string
  userVisibleValue: string | null
  data?: unknown
  /** Present on cells from GraphQL/cache when `columns` on the provider is empty. */
  column?: LookupProviderColumn
}

export interface LookupContextValue {
  getCellDisplay: (cell: MinimalLookupTableRowCell, columnId: string) => string | null
  getEnumSource: (cell: MinimalLookupTableRowCell, columnId: string) => LookupEnumSource | null
  isUnauth: boolean
  displayImagesInline: boolean
}

export const LookupContext = createContext<LookupContextValue | null>(null)

export function useLookupContext() {
  const context = useContext(LookupContext)
  if (!context) {
    throw new Error('useLookupContext must be used within a LookupProvider')
  }
  return context
}

/**
 * Prefer `userVisibleValue`; if missing, derive a string from structured `data` so formatting
 * matches (dates, numbers, etc.) without requiring a second network round-trip.
 */
function resolveDisplayStringFromCell(cell: MinimalLookupTableRowCell): string | null {
  const userVisibleValue = cell.userVisibleValue
  if (userVisibleValue != null && userVisibleValue !== '') {
    return userVisibleValue
  }
  if (!cell.data || !isTableCellData(cell.data)) {
    return null
  }
  const primitiveValue = getTableCellPrimitiveValue(cell.data)
  if (primitiveValue === null || primitiveValue === '') {
    return null
  }
  if (Array.isArray(primitiveValue)) {
    return primitiveValue.join(', ')
  }
  if (typeof primitiveValue === 'boolean') {
    return primitiveValue ? 'true' : 'false'
  }
  return String(primitiveValue)
}

/**
 * The values a cell holds. A multi-select arrives as an array in `data`, and each value needs its own mark,
 * so the already-joined `userVisibleValue` can't be the source of truth.
 */
export function resolveCellValues(cell: MinimalLookupTableRowCell): string[] {
  if (cell.data && isTableCellData(cell.data)) {
    const primitiveValue = getTableCellPrimitiveValue(cell.data)
    if (Array.isArray(primitiveValue)) {
      return primitiveValue.map(String)
    }
  }
  const displayString = resolveDisplayStringFromCell(cell)
  return displayString ? [displayString] : []
}

/** The column a cell is formatted by, falling back to the one the cell carries itself */
function resolveColumn(
  columns: LookupProviderColumn[] | undefined,
  cell: MinimalLookupTableRowCell,
  columnId: string,
): LookupProviderColumn | undefined {
  return (
    columns?.find((column) => column.id === columnId) ??
    (cell.column?.id === columnId ? cell.column : undefined)
  )
}

/**
 * How a lookup's value renders: chips when the lookup is an array or its source is a multi-select,
 * otherwise text with a mark. Null when the source isn't an enum.
 */
export function resolveLookupEnumRendering({
  enumSource,
  label,
  cell,
  isArray = false,
}: {
  enumSource: LookupEnumSource | null | undefined
  label: string
  cell?: MinimalLookupTableRowCell
  isArray?: boolean
}): LookupEnumRendering | null {
  if (!enumSource) {
    return null
  }

  if (!isArray && !enumSource.isMultiSelect) {
    return {
      kind: 'text',
      ...getEnumValueColorAndIcon(label, enumSource.options),
    }
  }

  const labels = enumSource.isMultiSelect && cell ? resolveCellValues(cell) : [label]

  return {
    kind: 'chips',
    values: labels.map((valueLabel) => ({
      label: valueLabel,
      ...getEnumValueColorAndIcon(valueLabel, enumSource.options),
    })),
  }
}

export function LookupProvider({
  children,
  isUnauth,
  columns,
  displayImagesInline,
}: PropsWithChildren<{
  isUnauth: boolean
  columns?: LookupProviderColumn[]
  displayImagesInline?: boolean
}>) {
  const { locale } = useGlobalContext()

  const getEnumSource = useCallback(
    (cell: MinimalLookupTableRowCell, columnId: string): LookupEnumSource | null =>
      enumSourceForLookupColumn(resolveColumn(columns, cell, columnId)),
    [columns],
  )

  const getCellDisplay = useCallback(
    (cell: MinimalLookupTableRowCell, columnId: string): string | null => {
      const column = resolveColumn(columns, cell, columnId)

      const resolvedDisplayString = resolveDisplayStringFromCell(cell)

      // Without column metadata we only format pass-through: derived string, else raw UVV (e.g. "").
      if (!column || !resolvedDisplayString) {
        return resolvedDisplayString ?? cell.userVisibleValue
      }

      const target = column.resolvedSourceColumn.column
      switch (target.type) {
        case DataType.BOOLEAN:
          return resolvedDisplayString === 'true' ? 'True' : 'False'

        case DataType.DATE:
        case DataType.FORMULA_DATE:
          return formatDateColumnValue(
            resolvedDisplayString,
            {
              formatOptions: target.formatOptions,
              formatPreset: target.formatPreset,
            },
            locale,
          )

        case DataType.DATE_TIME:
        case DataType.FORMULA_DATE_TIME: {
          // userVisibleValue loses the UTC 'Z' marker, so prefer cell.data which retains it.
          let valueToFormat = resolvedDisplayString
          const cellData = cell.data as Record<string, unknown>
          if (isTableCellData(cellData)) {
            const primitive = getTableCellPrimitiveValue(cellData)
            if (primitive !== null && primitive !== '' && typeof primitive === 'string') {
              valueToFormat = primitive
            }
          }
          return formatDateColumnValue(
            valueToFormat,
            {
              formatOptions: target.formatOptions,
              formatPreset: target.formatPreset,
              isDateTime: true,
            },
            locale,
          )
        }

        case DataType.FORMULA_NUMBER:
        case DataType.NUMBER:
          return formatNumberColumnValue(
            resolvedDisplayString,
            {
              formatOptions: target.formatOptions,
              formatPreset: target.formatPreset,
            },
            locale,
          )

        case DataType.FORMULA_BOOLEAN:
          // FORMULA_BOOLEAN is out of scope for the non-numeric formulas project
          return null

        case DataType.ARRAY:
        case DataType.ENUM:
        case DataType.FILE_URL:
        case DataType.ID:
        case DataType.LOOKUP:
        case DataType.STRING:
        case DataType.FORMULA_STRING:
        case DataType.USER:
          return resolvedDisplayString
      }
    },
    [columns, locale],
  )

  const value = {
    getCellDisplay,
    getEnumSource,
    isUnauth,
    displayImagesInline: displayImagesInline ?? false,
  }

  return <LookupContext.Provider value={value}>{children}</LookupContext.Provider>
}
